import dynamic from 'next/dynamic';
import { type MultiSelectComboBoxProps } from '@discover-ui/comboBox/multiSelectComboBox';
import useSuburbs from '@discover-data/stores/hooks/suburb/useSuburbs';
import { type SuburbType } from '@discover-data/gql/fragments/suburb';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';

const MultiSelectComboBox = dynamic(() => import('@discover-ui/comboBox/multiSelectComboBox'));

export type SuburbSelectProps = {
  suburbs: SuburbType[];
} & Omit<
  MultiSelectComboBoxProps,
  'options' | 'setValue' | 'placeholder' | 'description' | 'isSelected' | 'label'
>;

function getLabel({ length: count, 0: first }: SuburbType[]) {
  if (count === 0) return 'Any suburb';
  if (count === 1) return first.menuTitle || 'Any suburb';
  return `${count} suburbs`;
}

export default function SuburbSelect({ suburbs, triggerProps, ...props }: SuburbSelectProps) {
  const [selectedSuburbs, setSuburbs] = useSuburbs(suburbs);
  const selectedIds = new Set(selectedSuburbs.map((suburb) => suburb.id));

  function toggleSuburb(newValue: string | undefined) {
    if (!newValue || newValue === 'any') {
      setSuburbs([]);
      return;
    }
    const selectedSuburb = suburbs.find((suburb) => `${suburb.id}` === newValue);
    if (!selectedSuburb) return;

    const next = selectedIds.has(selectedSuburb.id)
      ? selectedSuburbs.filter((suburb) => suburb.id !== selectedSuburb.id)
      : [...selectedSuburbs, selectedSuburb];
    setSuburbs(next);
  }

  return (
    <TrackProvider button_name="suburb picker">
      <MultiSelectComboBox
        label={getLabel(selectedSuburbs)}
        isSelected={(value) =>
          value === 'any' ? selectedSuburbs.length === 0 : selectedIds.has(Number(value))
        }
        options={[
          {
            value: 'any',
            label: 'Any suburb',
          },
          ...suburbs
            .map((option) => ({
              value: `${option.id}`,
              label: option.menuTitle,
            }))
            .filter(
              (option): option is { value: string; label: string } =>
                !!option.value && !!option.label
            )
            .sort((optionA, optionB) => {
              if (optionA.label > optionB.label) {
                return 1;
              }
              if (optionA.label < optionB.label) {
                return -1;
              }
              return 0;
            }),
        ]}
        setValue={(value) => toggleSuburb(value)}
        placeholder="Pick a suburb"
        description="Select a suburb from the list"
        triggerProps={triggerProps}
        {...props}
      />
    </TrackProvider>
  );
}
